import { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { captureError } from '../lib/sentry';
import { isVersieSkewFout, magEenmaligHerladen } from '../lib/versieSkew';

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  /** Fout die op een verouderde/dubbel geladen bundel wijst (zie lib/versieSkew). */
  isSkewError: boolean;
  /** true = we hebben de automatische herlaad gestart; false = net al herladen, toon eerlijke tekst. */
  herlaadGestart: boolean;
}

/**
 * Versie-skew (na een deploy laadt een oud tabblad een lazy chunk die niet meer
 * bestaat, of — casus Pieter 01-10 — een chunk wordt dubbel geïnstantieerd en
 * React-context klopt niet meer): opnieuw renderen helpt niet, alleen een
 * volledige herlaad. We herladen daarom één keer automatisch (sessionStorage-
 * venster tegen een lus); gebeurt het daarna nóg een keer, dan zeggen we eerlijk
 * wat er aan de hand is in plaats van de rode "er is iets misgegaan"-kaart.
 */
function startSkewHerlaad(): boolean {
  let mag = true;
  try {
    mag = magEenmaligHerladen(sessionStorage);
  } catch {
    /* sessionStorage onbereikbaar (privémodus): dan gewoon herladen */
  }
  if (!mag) return false;
  const w = window as unknown as { __prachtHandleChunkError?: () => void };
  if (typeof w.__prachtHandleChunkError === 'function') {
    // Zelfde pad als index.html: wist de versie-cache en verwijdert de update-UI.
    w.__prachtHandleChunkError();
  } else {
    window.location.reload();
  }
  return true;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isSkewError: false, herlaadGestart: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, isSkewError: isVersieSkewFout(error) };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (isVersieSkewFout(error)) {
      const gestart = startSkewHerlaad();
      if (!gestart) {
        // Tweede keer kort na elkaar: wél loggen, dit is dan mogelijk geen deploy.
        captureError(error, {
          componentStack: errorInfo.componentStack ?? undefined,
          versieSkewHerlaadGeblokkeerd: true,
        });
      }
      this.setState({ herlaadGestart: gestart });
      return;
    }
    console.error('ErrorBoundary caught:', error, errorInfo);
    captureError(error, { componentStack: errorInfo.componentStack ?? undefined });
  }

  handleRetry = () => {
    if (this.state.isSkewError) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null, isSkewError: false, herlaadGestart: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isSkewError) {
        const bezig = this.state.herlaadGestart;
        return (
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="bg-surface border border-theme-border rounded-xl p-8 max-w-md w-full text-center">
              <RefreshCw
                className={`w-10 h-10 text-brand mx-auto mb-4 ${bezig ? 'animate-spin' : ''}`}
              />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {bezig ? 'Nieuwe versie laden…' : 'De app is bijgewerkt terwijl je werkte'}
              </h3>
              <p className="text-sm text-foreground-secondary mb-4">
                {bezig
                  ? 'Er is een nieuwe versie van de app. We vernieuwen automatisch. Gebeurt er niets? Klik hieronder.'
                  : 'Wat je net opsloeg is bewaard. Ververs de pagina om verder te werken in de nieuwe versie.'}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-dark text-white rounded-lg hover:bg-dark-secondary transition-colors text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                Vernieuwen
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="flex flex-col items-center justify-center py-16 px-6">
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-md w-full text-center">
            <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {this.props.fallbackMessage || 'Er is iets misgegaan'}
            </h3>
            <p className="text-sm text-foreground-secondary mb-4">
              Er is een onverwachte fout opgetreden. Probeer het opnieuw.
            </p>
            {this.state.error && (
              <pre className="text-xs text-left text-red-700 bg-red-100 rounded-lg p-3 mb-4 max-h-40 overflow-auto whitespace-pre-wrap break-words">
                {this.state.error.message}
                {'\n'}
                {this.state.error.stack?.split('\n').slice(1, 6).join('\n')}
              </pre>
            )}
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-dark text-white rounded-lg hover:bg-dark-secondary transition-colors text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Opnieuw proberen
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
